import React, {useState} from "react";
import {AnimatePresence, motion} from "framer-motion";
import styles, {C} from "../styles";
import Community from "../components/Community";
import {apiDeleteCommunity, apiGetCommunityPosts} from "../api/communities";
import {generateAvatar} from "../utils";
import {formatDistanceToNow} from "date-fns";
import {fr} from "date-fns/locale";
import {useConfirm} from "../components/FeedbackProvider";

function ThreadCard({community, onOpen, onDelete, isAdmin}) {
    const [preview, setPreview] = React.useState(null);
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
        apiGetCommunityPosts(community.id).then(posts => {
            if (!Array.isArray(posts) || posts.length === 0) return;
            setCount(posts.length);
            const last = posts[posts.length - 1];
            setPreview(last);
        });
    }, [community.id]);

    const avatarData = preview ? generateAvatar(preview.author) : null;

    return (
        <motion.div
            layout
            initial={{opacity: 0, y: 12}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -8}}
            whileHover={{y: -2, boxShadow: "0 4px 16px rgba(0,0,0,0.10)"}}
            transition={{duration: 0.18}}
            onClick={() => onOpen(community)}
            style={{
                backgroundColor: C.surface,
                border: `1px solid ${C.border}`,
                borderLeft: `4px solid ${C.accentWarm}`,
                borderRadius: 12,
                padding: "16px 20px",
                marginBottom: 12,
                cursor: "pointer",
            }}
        >
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12}}>
                <div style={{flex: 1}}>
                    <div style={{display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap"}}>
                        <span style={{fontSize: 18}}>💬</span>
                        <strong style={{fontSize: 16, color: C.text}}>{community.title}</strong>
                        {count > 0 && (
                            <span style={{
                                backgroundColor: C.accentWarmLight,
                                color: C.accentWarmDark,
                                fontSize: 12,
                                fontWeight: 700,
                                padding: "2px 8px",
                                borderRadius: 20,
                            }}>
                                {count} message{count > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                    {community.description && (
                        <p style={{margin: "0 0 10px", fontSize: 13, color: C.textMuted}}>{community.description}</p>
                    )}
                    {preview ? (
                        <div style={{
                            backgroundColor: "#FAFAF8",
                            border: `1px solid ${C.border}`,
                            borderRadius: 8,
                            padding: "8px 12px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                        }}>
                            <div style={{
                                ...styles.avatarCircle,
                                width: 30, height: 30, fontSize: 14, flexShrink: 0,
                                backgroundColor: avatarData.color,
                            }}>
                                {avatarData.avatar}
                            </div>
                            <div style={{flex: 1, minWidth: 0}}>
                                <span style={{fontSize: 12, fontWeight: 600, color: C.text}}>{preview.author} </span>
                                <span style={{fontSize: 12, color: C.textMuted}}>
                                    · {formatDistanceToNow(new Date(preview.created_at), {addSuffix: true, locale: fr})}
                                </span>
                                <p style={{
                                    margin: "2px 0 0",
                                    fontSize: 13,
                                    color: C.textMuted,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}>
                                    {preview.content}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <p style={{margin: 0, fontSize: 12, color: C.textMuted, fontStyle: "italic"}}>
                            Aucun message pour l'instant — soyez le premier !
                        </p>
                    )}
                </div>
                <div style={{display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8, flexShrink: 0}}>
                    <span style={{fontSize: 13, color: C.accentWarmDark, fontWeight: 600}}>Ouvrir →</span>
                    {isAdmin && (
                        <button
                            onClick={e => {
                                e.stopPropagation();
                                onDelete(community.id);
                            }}
                            style={{...styles.dangerButton, fontSize: 12, padding: "4px 10px"}}
                        >
                            Supprimer
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
}

const slideVariants = {
    enterFromRight: {x: "100%", opacity: 0},
    center: {x: 0, opacity: 1},
    exitToRight: {x: "100%", opacity: 0},
    enterFromLeft: {x: "-40%", opacity: 0},
    exitToLeft: {x: "-40%", opacity: 0},
};

export default function DiscussionsPage({communities, user, onRefresh}) {
    const confirm = useConfirm();
    const [selected, setSelected] = useState(null);
    const isAdmin = user?.role === "admin";

    function openThread(community) {
        setSelected(community);
    }

    function closeThread() {
        setSelected(null);
        onRefresh();
    }

    async function deleteCommunity(communityId) {
        if (!(await confirm("Supprimer ce fil de discussion ?", {danger: true}))) return;
        apiDeleteCommunity(communityId).then(onRefresh);
    }

    return (
        <div style={{overflow: "hidden", position: "relative"}}>
            <AnimatePresence mode="wait" initial={false}>
                {!selected ? (
                    <motion.div
                        key="list"
                        variants={slideVariants}
                        initial="enterFromLeft"
                        animate="center"
                        exit="exitToLeft"
                        transition={{duration: 0.25, ease: "easeInOut"}}
                    >
                        <h2 style={{marginTop: 0, marginBottom: 4, fontFamily: "'Fraunces', serif"}}>💬 Fils de discussion</h2>
                        <p style={{margin: "0 0 20px", fontSize: 13, color: C.textMuted}}>
                            {communities.length} fil{communities.length > 1 ? "s" : ""} — ouverts à tous les services Paroles d'Agents
                        </p>
                        {communities.length === 0 && (
                            <p style={{color: C.textMuted, fontStyle: "italic"}}>
                                Aucun fil de discussion ouvert pour l'instant. Les prochains sujets ouverts par vos représentants apparaîtront ici.
                            </p>
                        )}
                        {communities.map(community => (
                            <ThreadCard
                                key={community.id}
                                community={community}
                                onOpen={openThread}
                                onDelete={deleteCommunity}
                                isAdmin={isAdmin}
                            />
                        ))}
                    </motion.div>
                ) : (
                    <motion.div
                        key={`thread-${selected.id}`}
                        variants={slideVariants}
                        initial="enterFromRight"
                        animate="center"
                        exit="exitToRight"
                        transition={{duration: 0.25, ease: "easeInOut"}}
                    >
                        <button
                            onClick={closeThread}
                            style={{
                                ...styles.primaryButton,
                                marginBottom: 20,
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                fontSize: 13,
                                padding: "7px 14px",
                            }}
                        >
                            ← Retour aux fils
                        </button>
                        <Community community={selected} user={user}/>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
